<template>
  <div :class="classes">
    <p
      class="cordel-slider-tooltip"
      :style="styles"
    >
      <input
        v-if="editing"
        ref="input"
        :placeholder="placeholder"
        class="cordel-slider-input"
        type="text"
        @input="input"
      >

      <template v-else>
        <template v-if="label">
          {{ label }}:
        </template>

        {{ formatted }}
      </template>

      <button
        :disabled="disabled"
        class="cordel-slider-button"
        tabindex="-1"
        @click="toggle"
      >
        <font-awesome-icon
          class="cordel-slider-button-icon"
          :icon="editing ? 'check' : 'pen'"
        />
      </button>
    </p>

    <vue-slider
      ref="slider"

      :clickable="false"
      :disabled="disabled"
      :dot-size="22"
      :duration="0.1"
      :height="8"
      :lazy="true"
      :marks="marks"
      :max="max"
      :min="min"
      :silent="true"
      :value="value"
      class="cordel-slider-component"
      tooltip="none"
      @change="change"
      @dragging="dragging"
      @drag-end="dragged"
    >
      <template v-slot:label="options">
        <span
          class="cordel-slider-mark"
          @click="select(options.label)"
        >
          {{ options.label }}
        </span>
      </template>
    </vue-slider>
  </div>
</template>

<script>
  import VueSlider from 'vue-slider-component';

  export default {
    name: 'CordelSlider',
    components: {
      VueSlider,
    },
    props: {
      name: {
        type: String,
        default: '',
      },
      value: {
        type: Number,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },

      min: {
        type: Number,
        default: null,
      },
      max: {
        type: Number,
        default: null,
      },
      marks: {
        type: Object,
        default() {
          return {};
        },
      },

      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      formatter: {
        type: Function,
        default: x => x,
      },
    },
    data() {
      return {
        editing: false,
        temporary: null,
      };
    },
    computed: {
      formatted() {
        return this.formatter(this.temporary !== null ? this.temporary : this.value);
      },
      percentage() {
        return (this.temporary !== null ? this.temporary : this.value) * 100 / this.max;
      },
      placement() {
        if (!this.percentage || this.percentage < 35) {
          return 'right';
        }

        if (this.percentage > 65) {
          return 'left';
        }

        return 'center';
      },
      classes() {
        const classes = [
          'cordel-slider',
          `placement--${this.placement}`,
        ];

        if (this.editing) {
          classes.push('state--editing');
        }

        return classes;
      },
      styles() {
        return { left: `${this.percentage}%` };
      },
    },
    methods: {
      change(value) {
        this.$emit('change', value);
      },
      input(e) {
        // eslint-disable-next-line no-param-reassign
        e.target.value = this.formatter(e.target.value);
      },
      select(label) {
        this.editing = false;

        if (this.disabled) {
          return;
        }

        this.$refs.slider.setValue(
          parseInt(Object.keys(this.marks).find(mark => this.marks[mark] === label), 10),
        );
      },

      dragging(value) {
        this.temporary = value;
      },
      dragged() {
        this.temporary = null;
      },

      toggle() {
        const {
          input,
        } = this.$refs;

        this.editing = !this.editing;

        if (this.editing) {
          this.$nextTick(this.focus);
        } else if (input.value) {
          this.change(parseInt(input.value.replace(/\D/g, ''), 10));
        }
      },
      focus() {
        const {
          input,
        } = this.$refs;

        if (input) {
          input.value = '';
          input.focus();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-slider {
    padding: calc(
      var(--cordel-spacing-nano)
      + (var(--cordel-font-size-m) * var(--cordel-line-height-s))
      + var(--cordel-spacing-nano)
    ) 0;
    position: relative;
  }

  .cordel-slider-tooltip {
    align-items: center;
    background-color: var(--cordel-color-constant-light);
    border-radius: 99px;
    border: var(--cordel-border-width-soft) solid var(--cordel-color-neutral-light);
    box-shadow: var(--cordel-box-shadow-around-2);
    display: flex;
    flex-direction: row;
    font-size: var(--cordel-font-size-l);
    font-weight: var(--cordel-font-weight-bold);
    justify-content: center;
    line-height: var(--cordel-line-height-s);
    padding: var(--cordel-spacing-nano) var(--cordel-spacing-xxxs);
    position: absolute;
    top: 0;
    white-space: nowrap;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-font-size-m);
    }

    .placement--left & {
      margin-left: var(--cordel-spacing-xxxs);
      transform: translateX(-100%);
    }

    .placement--center & {
      transform: translateX(-50%);
    }

    .placement--right & {
      margin-left: calc(-1 * var(--cordel-spacing-xxxs));
      transform: translateX(0%);
    }
  }

  .cordel-slider-button {
    @include cordel-input-reset;

    background-color: var(--cordel-color-highlight-primary);
    border-radius: 50%;
    color: var(--cordel-color-constant-light);
    cursor: pointer;
    height: var(--cordel-spacing-xl);
    line-height: var(--cordel-spacing-xl);
    text-align: center;
    margin-left: var(--cordel-spacing-nano);
    margin-right: calc(-1 * (var(--cordel-spacing-xxxs) - var(--cordel-spacing-nano)));
    width: var(--cordel-spacing-xl);

    &[disabled] {
      background-color: var(--cordel-color-neutral-primary);
      cursor: not-allowed;
    }
  }

  .cordel-slider-button-icon {
    font-size: var(--cordel-font-size-m);

    .state--editing & {
      margin-bottom: -0.05em;
    }

    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-font-size-s);
    }
  }

  .cordel-slider-input {
    @include cordel-input-reset;

    font-size: inherit;
    font-weight: var(--cordel-font-weight-bold);
    margin: 0 var(--cordel-spacing-mini);
    width: 7em;
  }
</style>

<style lang="scss">
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-slider-component {
    &.vue-slider {
      box-sizing: content-box;
      margin-top: var(--cordel-spacing-xs);

      .vue-slider-rail {
        background-color: var(--cordel-color-neutral-primary);
        border-radius: 99px;
        height: var(--cordel-spacing-micro);
      }

      .vue-slider-process {
        background-color: var(--cordel-color-highlight-primary);
        border-radius: 99px;
        z-index: initial;
      }

      &.vue-slider-disabled {
        cursor: not-allowed;

        .vue-slider-process {
          background-color: var(--cordel-color-neutral-primary);
        }

        .vue-slider-dot-handle {
          cursor: not-allowed;
          background-color: var(--cordel-color-neutral-primary);
        }
      }

      .vue-slider-marks {
        top: var(--cordel-spacing-xxs);
      }

      .vue-slider-mark {
        color: var(--cordel-color-neutral-dark);
        font-size: var(--cordel-font-size-m);
        height: auto !important;
        line-height: var(--cordel-line-height-s);
        text-align: center;
        width: 1px !important;

        @media all and (max-width: $cordel-breakpoint-mobile) {
          font-size: var(--cordel-font-size-xs);
        }
      }

      .cordel-slider-mark {
        cursor: pointer;
        position: absolute;
        top: calc(var(--cordel-spacing-mini) + var(--cordel-spacing-nano));
        transform: translateX(-50%);
      }

      .vue-slider-mark-step {
        background-color: var(--cordel-color-neutral-dark);
        height: var(--cordel-spacing-nano);
        width: 100%;
      }

      .vue-slider-dot {
        z-index: initial;
      }

      .vue-slider-dot-handle {
        background-color: var(--cordel-color-highlight-primary);
        border-radius: 50%;
        border: var(--cordel-border-width-heavy) solid var(--cordel-color-constant-light);
        box-shadow: var(--cordel-box-shadow-around-1);
        cursor: pointer;
        height: 100%;
        position: relative;
        transition: box-shadow 0.1s, background-color 0.1s;
        width: 100%;
      }
    }
  }
</style>
